import React from 'react';
import { createRoot } from 'react-dom/client';
import Drawing from '../components/UI/drawing/drawing';
import { uploadImage } from '../api/account';
import { editDraperyOrderDesign } from '../api/drapery-order-design';
import { Config } from '../utils';

const waitForDrawingMount = async () => {
  await new Promise((resolve) => window.setTimeout(resolve, 0));
  await new Promise((resolve) => window.requestAnimationFrame(() => resolve(undefined)));
  await new Promise((resolve) => window.requestAnimationFrame(() => resolve(undefined)));
};

const getDrawingOrderType = (orderType?: string, design?: any): string | undefined => {
  if (orderType === 'roman-shade-order' || orderType === 'roman-shade') {
    return 'roman-shade';
  }

  const romanShadeFunction = Number(design?.romanShadeJson?.style?.function);
  if (romanShadeFunction === 0 || romanShadeFunction === 1) {
    return 'roman-shade';
  }

  return orderType;
};

const getRomanShadeValanceForDrawing = (romanShadeJson?: any): string | undefined => {
  const valanceAndReturns = romanShadeJson?.valanceAndReturns;
  if (!valanceAndReturns) {
    return undefined;
  }

  const { frontValance, sides } = valanceAndReturns;

  if (frontValance === 1 && (sides === 'returns' || sides === 'side-flaps')) {
    return 'valance-returns-wire';
  }

  if (frontValance === 1 && sides !== 'returns' && sides !== 'side-flaps') {
    return 'valance-no-returns-wire';
  }

  if (frontValance === 0 && sides === 'side-flaps') {
    return 'no-valance-returns-wire';
  }

  if (frontValance === 0 && sides === 'returns') {
    return 'no-valance-wrapped-returns';
  }

  return 'no-valance-no-returns-wire';
};

const createDrawingBlobFromDesign = async (design: any, orderType?: string): Promise<Blob | null> => {
  const host = document.createElement('div');
  host.style.position = 'fixed';
  host.style.left = '-10000px';
  host.style.top = '0';
  host.style.width = '720px';
  host.style.height = '720px';
  host.style.pointerEvents = 'none';
  host.style.opacity = '0';

  const tabsHost = document.createElement('div');
  tabsHost.id = 'react-tabs-1';
  tabsHost.style.width = '720px';
  tabsHost.style.height = '720px';
  host.appendChild(tabsHost);
  document.body.appendChild(host);

  const root = createRoot(tabsHost);
  const drawingRef = React.createRef<Drawing>();
  const drawingOrderType = getDrawingOrderType(orderType, design);

  try {
    root.render(
      React.createElement(Drawing, {
        ref: drawingRef,
        shapes: design?.drawingShapeJson || [],
        category: design?.category ?? -1,
        panelType: design?.panelType,
        panelJson: design?.panelJson || {},
        rodWidth: design?.rodWidth,
        finishedLengthOfPanels: design?.finishedLengthOfPanels ?? 0,
        disabled: true,
        orderType: drawingOrderType,
        valanceForDrawing:
          drawingOrderType === 'roman-shade'
            ? getRomanShadeValanceForDrawing(design?.romanShadeJson)
            : undefined,
        romanShadeJson: design?.romanShadeJson,
        onShapeChanged: () => undefined,
      })
    );

    await waitForDrawingMount();
    return (await drawingRef.current?.convertToImage()) || null;
  } finally {
    root.unmount();
    host.remove();
  }
};

export const regenerateWorkOrderDrawing = async ({
  accountId,
  design,
  orderType,
}: {
  accountId: number;
  design: any;
  orderType?: string;
}): Promise<string | null> => {
  if (!design?.id) {
    return null;
  }

  const drawingBlob = await createDrawingBlobFromDesign(design, orderType);
  if (!drawingBlob) {
    return null;
  }

  const formData = new FormData();
  formData.append('file', new File([drawingBlob], 'work-order-drawing.png', { type: 'image/png' }));
  formData.append('accountId', accountId.toString());
  formData.append('fileStoreUrl', Config.fileStoreUrl);

  const uploadResponse = await uploadImage(formData);
  if (uploadResponse?.error || !uploadResponse?.url) {
    return null;
  }

  await editDraperyOrderDesign({
    ...design,
    drawing: uploadResponse.url,
  });

  return uploadResponse.url;
};
